import styled, { css } from 'styled-components';
import type { TTableCellContent } from '../atoms';
import { Loading, TableCellContent, TransparentIconButton } from '../atoms';
import type { PromiseOr } from 'simplytyped';
import type { PropsWithChildren } from 'react';
import { isNil } from 'lodash';

type TTable = {
  children: React.ReactNode;
};

type TTableCell = {
  breakAll?: boolean;
  compact?: boolean;
};

type TTableRow = {
  hr?: boolean;
};

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
`;

const SimpleTable = ({ children }: TTable) => {
  return (
    <Table>
      <tbody>{children}</tbody>
    </Table>
  );
};

const TableRow = styled.tr<TTableRow>(
  ({ theme, hr }) => css`
    text-align: left;

    ${hr &&
    css`
      border-top: ${theme.borders.light};
    `}
  `
);

export const TableHead = styled.th<{ width?: string; compact?: boolean }>(
  ({ theme, width = '240px', compact = false }) => css`
    width: ${width};
    padding-right: ${theme.paddings.sm};
    padding-top: ${!compact ? theme.paddings.sm : theme.paddings.xs};
    padding-bottom: ${!compact ? theme.paddings.sm : theme.paddings.xs};
    border-right: ${theme.borders.normal};
    vertical-align: top;
    line-height: 22px;

    @media (max-width: ${theme.breakpoints.max.sm}) {
      width: 175px;
    }

    @media (max-width: ${theme.breakpoints.max.xs}) {
      width: 25%;
    }
  `
);

const TableCell = styled.td<TTableCell>(
  ({ theme, breakAll, compact = false }) => css`
    text-align: left;
    padding-left: ${theme.paddings.md};
    padding-top: ${!compact ? theme.paddings.sm : theme.paddings.xs};
    padding-bottom: ${!compact ? theme.paddings.sm : theme.paddings.xs};
    word-break: ${breakAll ? 'break-all' : 'break-word'};
    vertical-align: top;
  `
);

const CellContentFlexWrap = styled.span(
  ({ theme }) => css`
    display: flex;
    justify-content: space-between;

    &:first-child {
      flex-grow: 1;
    }
  `
);

type RowProps = {
  title: string;
  headerCellWidth?: string;
  check?: boolean;
  checked?: boolean;
  array?: ReadonlyArray<string>;
  address?: object;
  breakAll?: boolean;
  loading?: boolean;
  compact?: boolean;
  onEdit?: (value: TTableCellContent['content']) => PromiseOr<unknown>;
} & TTableCellContent;

const SimpleTableRow = ({
  title,
  breakAll,
  loading,
  headerCellWidth,
  onEdit,
  children,
  compact = false,
  ...rest
}: PropsWithChildren<RowProps>) => {
  const { content, contentType, options } = {
    content: undefined,
    ...(rest as TTableCellContent)
  };
  const cellContent = {
    contentType,
    options,
    content
  } as TTableCellContent;

  return (
    <TableRow hr={contentType === 'hr'}>
      <TableHead compact width={headerCellWidth}>
        {title}
      </TableHead>
      <TableCell compact breakAll={breakAll}>
        {loading ? (
          <Loading small />
        ) : (
          <CellContentFlexWrap>
            {isNil(children) ? <TableCellContent {...cellContent} /> : children}

            {onEdit && (
              <TransparentIconButton
                type={'button'}
                onClick={() => onEdit(content)}
              >
                <span className="material-symbols-rounded">edit</span>
              </TransparentIconButton>
            )}
          </CellContentFlexWrap>
        )}
      </TableCell>
    </TableRow>
  );
};

export { SimpleTable, SimpleTableRow };
